import {
  ButtonColor,
  PrimaryButton,
} from 'component-library/components/buttons/PrimaryButton';
import { CurrencyInput } from 'component-library/components/inputs/CurrencyInput';
import { DateInput } from 'component-library/components/inputs/DateInput';
import { NumberInput } from 'component-library/components/inputs/NumberInput';
import { Flex } from 'component-library/components/layout/Flex';
import { Tooltip } from 'component-library/components/tooltip/Tooltip';
import { Body } from 'component-library/components/typography/Body';
import React, { FC, useState } from 'react';
import { device } from 'shared/theme';
import styled from 'styled-components';

const StyledTooltip = styled(Tooltip)`
  margin-left: 10px;
`;

const StyledDateInput = styled(DateInput)`
  flex: 3;
`;

const StyledCurrencyInput = styled(CurrencyInput)`
  flex: 3;
`;

const InputContainer = styled(Flex).attrs({ alignItems: 'center' })`
  ${`@media ${device.sm}`} {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const StyledBody = styled(Body).attrs({
  variant: 2,
})`
  min-width: 250px;
  display: flex;
`;

interface Errors {
  dateOfBirth: boolean;
  retirementAge: boolean;
  retirementSavings: boolean;
}

export interface CalculatorFormInputData {
  dateOfBirth: string;
  retirementAge: number;
  retirementSavings: number;
}

interface CalculatorFormInputsProps {
  onSubmit: (data: CalculatorFormInputData) => void;
}

export const CalculatorFormInputs: FC<CalculatorFormInputsProps> = ({
  onSubmit,
}) => {
  const [dateOfBirth, updateDateOfBirth] = useState('');
  const [retirementSavings, updateRetirementSavings] = useState<
    number | undefined
  >();
  const [retirementAge, updateRetirementAge] = useState<number | null>(null);
  const [errors, setErrors] = useState<Errors>({
    dateOfBirth: false,
    retirementAge: false,
    retirementSavings: false,
  });

  const onFormSubmit = () => {
    if (dateOfBirth && retirementSavings && retirementAge) {
      onSubmit({ retirementSavings, retirementAge, dateOfBirth });
    } else {
      setErrors({
        dateOfBirth: !dateOfBirth,
        retirementSavings: !retirementSavings,
        retirementAge: !retirementAge,
      });
    }
  };

  return (
    <>
      <InputContainer marginBottom={32}>
        <StyledBody variant={2}>
          Birthday
          <StyledTooltip message="We use this to begin estimating your Required Minimum Distributions (RMDs)." />
        </StyledBody>
        <StyledDateInput
          value={dateOfBirth}
          onChange={(val) => {
            updateDateOfBirth(val);
            setErrors({ ...errors, dateOfBirth: !val });
          }}
          onBlur={(val) => setErrors({ ...errors, dateOfBirth: !val })}
          error={errors.dateOfBirth}
        />
      </InputContainer>
      <InputContainer marginBottom={32}>
        <StyledBody>
          Retirement savings
          <StyledTooltip message="Total balance of your 401(k), IRA, etc." />
        </StyledBody>
        <StyledCurrencyInput
          value={retirementSavings}
          onChange={(val) => {
            updateRetirementSavings(val);
            setErrors({ ...errors, retirementSavings: !val });
          }}
          onBlur={(e) =>
            setErrors({ ...errors, retirementSavings: !e.target.value })
          }
          error={!!errors.retirementSavings}
          placeholder="$"
        />
      </InputContainer>
      <InputContainer marginBottom={32}>
        <StyledBody>
          Retirement age
          <StyledTooltip message="Not sure? An estimate is fine!" />
        </StyledBody>
        <NumberInput
          value={retirementAge}
          onChange={(val) => {
            updateRetirementAge(val);
            setErrors({ ...errors, retirementAge: !val });
          }}
          onBlur={(e) =>
            setErrors({ ...errors, retirementAge: !e.target.value })
          }
          inputWidth={1}
          error={errors.retirementAge}
        />
      </InputContainer>
      {Object.values(errors).some((e) => e) && (
        <Flex alignItems="center">
          <Body variant={4}>Please fix the inputs in red and re-submit</Body>
        </Flex>
      )}
      <PrimaryButton buttonColor={ButtonColor.Peach} onClick={onFormSubmit}>
        See Your Paycheck Estimate
      </PrimaryButton>
    </>
  );
};
